import { useRouter } from '@townsquare/router/useRouter';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { Stack, xcss } from '@atlaskit/primitives';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/side-nav-items/flyout-menu-item';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import React from 'react';
import { useIntl } from 'react-intl';

import { AnalyticsSideNavItem, useSendSideNavItemClick } from '@townsquare/analytics-nav4';
import { useAppsByProduct } from '@townsquare/apps-store/hooks';
import { APP_ITEM_ID_TO_WORKLENS, SideNavAppItem as SideNavAppItemID } from '@townsquare/navigation-config/nav4';
import { fg } from '@townsquare/stat-sig/gate';

import type { CommonSideNavItemProps } from '../../../types';

const flyoutStyles = xcss({
	maxWidth: '400px',
	maxHeight: '476px',
});

/**
 * This component handles rendering any app item in the customizable sidebar.
 *
 * It gets most of its configuration and data from `item`, e.g. the name and Icon.
 *
 * `products` is passed in with a list of product instances, to determine if the item should be hidden, individual, or a flyout menu.
 */
export const SideNavAppItem = (props: CommonSideNavItemProps) => {
	const { elemAfter, item } = props;
	const intl = useIntl();
	const [routerStore] = useRouter();
	const sendSideNavItemClick = useSendSideNavItemClick();
	const isSameTabAlignmentEnabled = fg('townsquare-same-tab-alignment-gcko-849');

	const worklensAppId = APP_ITEM_ID_TO_WORKLENS[item.id as SideNavAppItemID];
	const products = useAppsByProduct()[worklensAppId];

	if (!products || products.length === 0) {
		return null;
	}

	const linkLabel = intl.formatMessage({
		id: 'townsquare.atlassian-home.side-nav.open-in-new-tab',
		description:
			'Label on an external link icon for links to products in the side nav which will open the link in a new tab',
		defaultMessage: 'Open in new tab',
	});

	if (products.length === 1) {
		return (
			<LinkMenuItem
				elemBefore={<item.Icon label="" />}
				elemAfter={
					elemAfter || isSameTabAlignmentEnabled ? undefined : (
						<LinkExternalIcon label={linkLabel} color="currentColor" spacing="spacious" size="small" />
					)
				}
				href={products[0].url}
				target={isSameTabAlignmentEnabled ? '_self' : '_blank'}
				onClick={() => {
					sendSideNavItemClick('navigationItem', AnalyticsSideNavItem.VIEW_ALL_APPS, {
						screen: routerStore.route.name,
						componentType: 'customItem',
						itemType: 'product',
						itemId: item.analyticsSubject,
					});
				}}
			>
				{products[0].productName || item.name}
			</LinkMenuItem>
		);
	}

	return (
		<FlyoutMenuItem>
			<FlyoutMenuItemTrigger
				elemBefore={<item.Icon label="" />}
				onClick={() => {
					// `button` actionSubject because this is just opening the flyout, not navigating anywhere.
					sendSideNavItemClick('button', AnalyticsSideNavItem.VIEW_ALL_APPS, {
						screen: routerStore.route.name,
						componentType: 'customItem',
						itemType: 'product',
						itemId: item.analyticsSubject,
					});
				}}
			>
				{item.name}
			</FlyoutMenuItemTrigger>
			<FlyoutMenuItemContent>
				<Stack xcss={flyoutStyles}>
					{products.map(product => (
						<LinkMenuItem
							key={product.siteName}
							href={product.url}
							target={isSameTabAlignmentEnabled ? undefined : '_blank'}
							elemBefore={<item.Icon label="" />}
							elemAfter={
								!isSameTabAlignmentEnabled && (
									<LinkExternalIcon label={linkLabel} color="currentColor" spacing="spacious" size="small" />
								)
							}
							onClick={() => {
								sendSideNavItemClick('navigationItem', AnalyticsSideNavItem.VIEW_ALL_APPS, {
									screen: routerStore.route.name,
									componentType: 'customItem',
									itemType: 'product',
									itemId: item.analyticsSubject,
								});
							}}
						>
							{product.siteName}
						</LinkMenuItem>
					))}
				</Stack>
			</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
};
